/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material'
import {
  fireBookingCreated,
  fireQuoteSelected,
  fireQuotesShown,
  flattenGroupedPrices,
} from 'analytics/bookingEvents'
import { VATTotalPrice } from 'api/common-types'
import type { BookingPrecheckResponse } from 'api/models/BookingPrecheck.type'
import { JourneyType } from 'app/constants/Booking/BookingConstants'
import { BookingSummary } from 'components/Bookings/BookingSummary/BookingSummary'
import {
  mergeObjects,
  multiplyValues,
} from 'components/Common/ObjectManagement/ObjectManagement'
import { StepComponentProps } from 'components/RouteWizard/RouteWizard'
import { useWizard } from 'components/RouteWizard/Wizard'
import useBooking from 'hooks/useBooking'
import useClient from 'hooks/useClient'
import useTravelPolicy from 'hooks/useTravelPolicy'
import { useEffect, useRef, useState } from 'react'
import { confirmAlert } from 'react-confirm-alert'
import { useSelector } from 'react-redux'
import QuoteListing from './QuoteListing'

const SURFACE = 'quote_old'

interface GSOProps extends StepComponentProps {
  quoteData: {
    prices: any
    reference: any
    inboundReference: any
    isLoading: any
    error: any
  }
  hideSummary?: boolean
}

const GroupedSupplierOptions = ({ quoteData, onSuccess, hideSummary }: GSOProps) => {
  const { data } = useWizard()
  const theme = useTheme()
  const client = useClient(data.account.clientId)
  const quickQuote = useSelector((state: any) => state.bookings.quickQuote)
  const { precheckWizardBooking, buildPolicyMessage, buildPolicyDialogText } =
    useTravelPolicy()
  const [selectMultiple, setSelectMultiple] = useState(false)
  const [selectedVehicles, setSelectedVehicles] = useState<{
    [key: string]: any
  }>({})
  const [canSelectMoreCars, setCanSelectMoreCars] = useState(true)
  const [showSeatCountWarning, setShowSeatCountWarning] = useState(false)
  const [spendPolicyLimit, setSpendPolicyLimit] = useState(false)
  const [showPolicyDialog, setShowPolicyDialog] = useState(false)
  const [policyResult, setPolicyResult] =
    useState<BookingPrecheckResponse | null>(null)
  const [cachedPolicyMessage, setCachedPolicyMessage] = useState<ReturnType<
    typeof buildPolicyMessage
  > | null>(null)
  const [preview, setPreview] = useState({
    ...data.preview,
    estimatedPriceCurrency: '',
    estimatedPrice: 0,
    selectedVehicle: null,
  })

  const { prices, reference, inboundReference, isLoading, error } = quoteData

  const {
    bookingData,
    createBooking,
    isLoading: isLoadingBooking,
    error: bookingError,
    errorMessage,
  } = useBooking()
  console.log(client)
  useEffect(() => {
    if (bookingError) {
      confirmAlert({
        customUI: ({ onClose }) => {
          if (
            errorMessage ===
              'Booking policy job spend limit would exceed booking policy rules, please reach out to an authorised user to make this booking' &&
            client?.clientThemeName === 'Veezu'
          ) {
            setSpendPolicyLimit(true)
          }
          return (
            <Dialog
              open={true}
              onClose={onClose}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle
                id="alert-dialog-title"
                sx={(theme) => ({
                  backgroundColor: '#ee000e',
                  color: theme.palette.info.contrastText,
                  fontSize: 'var(--fs-24, 1.5rem)',
                })}
              >
                Problem with booking
              </DialogTitle>
              <Typography variant="body1" sx={{ paddingY: 3, paddingX: 5 }}>
                {spendPolicyLimit ? (
                  <Typography sx={{ textWrap: 'pretty' }}>
                    Your booking is over the allowed limit. Please call{' '}
                    <a href="tel:+441743294579">01743 294579</a> to arrange your
                    booking.
                  </Typography>
                ) : (
                  errorMessage
                )}
              </Typography>

              <DialogActions>
                <Button onClick={onClose}>Close</Button>
              </DialogActions>
            </Dialog>
          )
        },
      })
    }
  }, [bookingError, createBooking])

  const pendingBookingRef = useRef<{
    supplierQuoteReference?: string
    vehicleType?: string
  }>({})
  const bookingCreatedFiredRef = useRef<string | null>(null)
  useEffect(() => {
    if (bookingData) {
      const bookingId = (bookingData as any)?.id
      const bookingRef = (bookingData as any)?.bookingReference
      if (bookingRef && bookingCreatedFiredRef.current !== bookingRef) {
        bookingCreatedFiredRef.current = bookingRef
        const toNum = (v: any) =>
          typeof v === 'number'
            ? Number(v.toFixed(2))
            : typeof v === 'string' && v.length
            ? Number(parseFloat(v).toFixed(2))
            : undefined
        const full = preview?.estimatedPriceFull as any
        fireBookingCreated({
          surface: SURFACE,
          bookingId,
          bookingReference: bookingRef,
          quoteGroupId: reference,
          quoteGroupIdInbound: inboundReference,
          supplierQuoteReference: pendingBookingRef.current.supplierQuoteReference,
          vehicleType:
            pendingBookingRef.current.vehicleType ?? preview?.selectedVehicle,
          winningPrice: toNum(full?.total ?? full),
          winningPriceExVat: toNum(full?.price),
          currency: full?.currency ?? preview?.estimatedPriceCurrency,
          journeyType: data?.plan?.journeyTypeName,
          bookingType: data?.type,
        })
      }
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview,
      })
    }
  }, [bookingData])

  const quotesShownFiredRef = useRef<string | null>(null)
  useEffect(() => {
    if (!prices || !reference) return
    if (quotesShownFiredRef.current === reference) return
    quotesShownFiredRef.current = reference
    fireQuotesShown({
      surface: SURFACE,
      quoteGroupId: reference,
      quoteGroupIdInbound: inboundReference,
      journeyType: data?.plan?.journeyTypeName,
      bookingType: data?.bookingType,
      passengers: data?.account?.numberOfPassengers,
      quotes: flattenGroupedPrices(prices),
    })
  }, [prices, reference, inboundReference])

  const findQuoteByReference = (
    ref?: string,
  ): { quote?: any; group?: any[] } => {
    if (!ref || !prices) return {}
    for (const group of Object.values(prices as any) as any[]) {
      if (!Array.isArray(group)) continue
      const found = group.find((q: any) => q?.supplierQuoteReference === ref)
      if (found) return { quote: found, group }
    }
    return {}
  }

  const findVehicleByRef = (ref: string | undefined): any => {
    if (!ref || !prices) return undefined
    const groups = Array.isArray(prices) ? [prices] : Object.values(prices)
    for (const group of groups as any[]) {
      const match = (group as any[]).find(
        (v: any) => v?.supplierQuoteReference === ref,
      )
      if (match) return match
    }
    return undefined
  }

  const collectCancellationPolicies = (...sources: any[]): string[] => {
    const out: string[] = []
    const seen = new Set<string>()
    for (const s of sources) {
      if (!s || typeof s !== 'string') continue
      const trimmed = s.trim()
      if (!trimmed || seen.has(trimmed)) continue
      seen.add(trimmed)
      out.push(trimmed)
    }
    return out
  }

  const handleBookNow = (
    supplierQuoteReference: string,
    numberOfSeats: number,
    estimatedPriceCurrency: string,
    price: number,
    title: string,
    inboundSupplierQuoteReference?: string,
  ) => {
    setShowSeatCountWarning(false)

    const selectedVehicleRow = findVehicleByRef(supplierQuoteReference)
    const cancellationPolicies = collectCancellationPolicies(
      selectedVehicleRow?.cancellationPolicy,
      selectedVehicleRow?.inboundCancellationPolicy,
    )

    setPreview((prevValue: any) => ({
      ...prevValue,
      estimatedPriceCurrency: estimatedPriceCurrency,
      estimatedPriceFull: price,
      selectedVehicle: title,
      cancellationPolicies,
    }))

    if (!supplierQuoteReference) {
      return
    }

    if (numberOfSeats < data?.account?.numberOfPassengers) {
      return setShowSeatCountWarning(true)
    }

    const { quote: selectedQuote, group } = findQuoteByReference(
      supplierQuoteReference,
    )
    if (selectedQuote) {
      fireQuoteSelected({
        surface: SURFACE,
        quoteGroupId: reference,
        selectedQuote,
        groupQuotes: group,
        journeyType: data?.plan?.journeyTypeName,
      })
    }

    const bookingData: any = {
      outbound: {
        quoteReference: reference,
        vehicles: [
          {
            supplierQuoteReference,
            quantity: 1,
          },
        ],
      },
    }

    if (
      inboundSupplierQuoteReference &&
      data.plan?.journeyTypeName === JourneyType.RETURN
    ) {
      bookingData.inbound = {
        quoteReference: inboundReference,
        vehicles: [
          {
            supplierQuoteReference: inboundSupplierQuoteReference,
            quantity: 1,
          },
        ],
      }
    }

    if (quickQuote || hideSummary) {
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview: {
          ...preview,
          estimatedPriceCurrency,
          estimatedPriceFull: price,
          selectedVehicle: title,
          cancellationPolicies,
        },
      })
    } else {
      pendingBookingRef.current = {
        supplierQuoteReference,
        vehicleType: title,
      }
      createBooking(bookingData)
    }
  }

  const handleMultiBooking = () => {
    let totalSeatCount = 0

    for (const [key, value] of Object.entries(selectedVehicles)) {
      const seatCount = value?.seats * value?.quantity
      totalSeatCount += seatCount
    }

    if (data.account?.numberOfPassengers > totalSeatCount) {
      setShowSeatCountWarning(true)
      return null
    }
    const vehicles = []
    for (const [key, value] of Object.entries(selectedVehicles)) {
      vehicles.push(value)
    }

    const bookingData: any = {
      outbound: {
        quoteReference: reference,
        vehicles,
      },
    }

    if (data.plan?.journeyTypeName === JourneyType.RETURN) {
      bookingData.inbound = {
        quoteReference: inboundReference,
        vehicles: vehicles.map((v: any) => ({
          ...v,
          supplierQuoteReference: v.inboundSupplierQuoteReference,
        })),
      }
    }

    const cancellationPolicies = collectCancellationPolicies(
      ...Object.values(selectedVehicles).flatMap((v: any) => [
        v?.cancellationPolicy,
        v?.inboundCancellationPolicy,
      ]),
    )

    if (quickQuote || hideSummary) {
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview: {
          ...preview,
          cancellationPolicies,
        },
      })
    } else {
      const firstVehicle = vehicles[0] as any
      pendingBookingRef.current = {
        supplierQuoteReference: firstVehicle?.supplierQuoteReference,
        vehicleType: firstVehicle?.vehicleTypeName,
      }
      createBooking(bookingData)
    }
  }

  const closePolicyDialog = () => {
    setShowPolicyDialog(false)
    setPolicyResult(null)
    setCachedPolicyMessage(null)
  }

  const handlePrecheckSubmit = async () => {
    try {
      const precheckResult = await precheckWizardBooking({
        estimatedPrice: preview?.estimatedPriceFull?.total,
      })

      setPolicyResult(precheckResult)
      setCachedPolicyMessage(
        buildPolicyMessage(precheckResult, { locale: 'en-GB' }),
      )
      setShowPolicyDialog(true)
    } catch (error) {
      console.error('Precheck failed:', error)
      setShowPolicyDialog(false)
    }
  }

  useEffect(() => {
    let estimatedPriceFull: VATTotalPrice = {
      price: 0,
      currency: '',
      vAT: 0,
      total: 0,
    }
    let totalVehicleCount = 0
    let selectedVehicle: any = Object.values(selectedVehicles)[0]

    for (const [key, value] of Object.entries(selectedVehicles)) {
      const vehicleCost = multiplyValues(value?.price, value?.quantity)
      estimatedPriceFull = mergeObjects(estimatedPriceFull, vehicleCost, {
        exception: 'currency',
      })
      totalVehicleCount += value?.quantity
    }

    setCanSelectMoreCars(data.account?.numberOfPassengers > totalVehicleCount)
    setPreview((prevValue: any) => ({
      ...prevValue,
      estimatedPriceFull,
      selectedVehicle: selectedVehicle
        ? `${selectedVehicle.title}${totalVehicleCount > 1 ? ' +' : ''}`
        : null,
    }))
  }, [selectedVehicles])

  if (isLoading) {
    return (
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <Typography align="center" paragraph>
          Getting your quotes, please wait.
        </Typography>
        <CircularProgress size={12} />
      </Box>
    )
  }

  if (!isLoading && !error && !prices) {
    return (
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={7}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography align="center" paragraph>
              Sorry there are no quotes available for your journey.
            </Typography>
          </Box>
        </Grid>
        {!hideSummary && (
          <Grid item xs={5} sx={{ alignSelf: 'flex-start' }}>
            <Box
              component="div"
              sx={{
                position: 'sticky',
                top: '20px',
              }}
            >
              <BookingSummary
                bookingPreview={preview}
                isLoadingBooking={isLoadingBooking}
                onSubmit={handleMultiBooking}
                onPrecheckSubmit={handlePrecheckSubmit}
              />
            </Box>
          </Grid>
        )}
      </Grid>
    )
  }

  return (
    <>
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={hideSummary ? 12 : 6}>
          <Box
            component="div"
            sx={{ float: hideSummary ? 'none' : 'right', width: '100%', maxWidth: hideSummary ? 'none' : '550px' }}
          >
            {!isLoading &&
              !error &&
              prices &&
              Object.entries(prices).map((key: any) => (
                <QuoteListing
                  key={key[0]}
                  vehicleDetails={key[1][0].vehicleDetails}
                  quotes={key[1]}
                  isImmediateJourney={data.plan?.isImmediateJourney}
                  handleBookNow={handleBookNow}
                  selectMultiple={selectMultiple}
                  selectedVehicles={selectedVehicles}
                  setSelectedVehicles={setSelectedVehicles}
                  canSelectMoreCars={canSelectMoreCars}
                />
              ))}

            {!selectMultiple && data?.account?.numberOfPassengers > 1 && (
              <Button
                onClick={() => setSelectMultiple((prevValue) => !prevValue)}
                sx={{
                  width: '350px',
                  '@media (min-width: 1550px)': {
                    width: '100%',
                    maxWidth: '730px',
                  },
                }}
              >
                Select Multiple Vehicles
              </Button>
            )}
            {hideSummary && selectMultiple && (() => {
              const totalSelected = Object.values(selectedVehicles).reduce(
                (acc: number, v: any) => acc + (Number(v?.quantity) || 0),
                0,
              )
              return (
                <Box
                  sx={(theme) => ({
                    position: 'sticky',
                    bottom: 0,
                    marginTop: '16px',
                    marginX: '-24px',
                    marginBottom: '-24px',
                    padding: '16px 24px',
                    backgroundColor: theme.palette.common.white,
                    borderTop: `1px solid ${theme.palette.grey[100]}`,
                    zIndex: 10,
                  })}
                >
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={isLoadingBooking || totalSelected === 0}
                    onClick={handleMultiBooking}
                    sx={(theme) => ({
                      padding: '12px 24px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '15px',
                      borderRadius: 3,
                      backgroundColor: theme.palette.secondary.main,
                      color: theme.palette.brand.dark,
                      '&:hover': {
                        backgroundColor: theme.palette.secondary.main,
                        opacity: 0.9,
                      },
                    })}
                  >
                    Book {totalSelected} {totalSelected === 1 ? 'Car' : 'Cars'}
                  </Button>
                </Box>
              )
            })()}
          </Box>
        </Grid>
        {!hideSummary && (
          <Grid item xs={6} sx={{ alignSelf: 'flex-start' }}>
            <Box
              component="div"
              sx={{
                position: 'sticky',
                top: '20px',
              }}
            >
              <BookingSummary
                bookingPreview={preview}
                isLoadingBooking={isLoadingBooking}
                onSubmit={handleMultiBooking}
                onPrecheckSubmit={handlePrecheckSubmit}
              />
              {client?.clientThemeName === 'Jyrney' && (
                <Box
                  component="div"
                  sx={{
                    fontSize: '12px',
                    fontStyle: 'italic',
                    color: '#414042',
                    margin: 'auto',

                    paddingTop: '20px',
                    width: '60%',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '5px',
                    textAlign: 'center',
                  }}
                >
                  By proceeding with your booking you agree to{' '}
                  <Link
                    href="https://jyrney.com/terms-and-conditions"
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textWrap: 'nowrap' }}
                  >
                    {client.clientThemeName}&apos;s Terms & Conditions
                  </Link>
                  {'. '}
                  {preview.displayVATPrice
                    ? 'Pricing includes VAT, but may be subject to waiting time, airport drop off/collection fees, parking, tolls, clean air charges and may be subject to congestion zone charges'
                    : 'Pricing excludes VAT, waiting time, airport drop off/collection fees, parking, tolls, clean air charges and may be subject to congestion zone charges'}
                </Box>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
      <Snackbar
        open={showSeatCountWarning}
        autoHideDuration={6000}
        onClose={() => setShowSeatCountWarning(false)}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.5 }}
            onClick={() => setShowSeatCountWarning(false)}
          >
            <CloseIcon />
          </IconButton>
        }
      >
        <Alert severity="error">
          You have not selected enough cars for your journey.
        </Alert>
      </Snackbar>
      <Dialog
        open={showPolicyDialog}
        onClose={closePolicyDialog}
        aria-labelledby="booking-policy-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="booking-policy-dialog-title">
          Booking Policy Check
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
            {policyResult
              ? buildPolicyDialogText(policyResult, { locale: 'en-GB' })
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePolicyDialog} variant="contained">
            Go back
          </Button>
          {policyResult?.overridable || policyResult?.allowed ? (
            <Button
              sx={{ background: theme.palette.primary.main }}
              variant="contained"
              onClick={() => {
                closePolicyDialog()
                handleMultiBooking()
              }}
            >
              Book now
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default GroupedSupplierOptions
